// src/App.js - VERSIÓN FINAL CORREGIDA (PERSISTENCIA DE TEMA)
import React, { Suspense, lazy, useState, useMemo, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/ExecutiveDashboardPage';
import ProjectManagement from './pages/ProjectManagement';
import ClientManagement from './pages/ClientManagement';
import WorkCenterPage from './pages/WorkCenterPage';
import NotificationCenter from './pages/NotificationCenter';
import ChatPage from './pages/ChatPage';
import CalendarPage from './pages/CalendarPage';
import UserManagement from './pages/UserManagement';
import SettingsPage from './pages/SettingsPage';
import DocumentManagement from './pages/DocumentManagement';
import ProfilePage from './pages/ProfilePage';
import ReportGenerator from './pages/ReportGenerator';
import ActivityLog from './pages/ActivityLog';
import FinanceManagement from './pages/FinanceManagement';
import EmailHistoryPage from './pages/EmailHistoryPage';
import RolesPermissions from './pages/RolesPermissions';
import ExpenseTicketsPage from './pages/ExpenseTicketsPage';
import PartnerPortalPage from './pages/PartnerPortalPage';
import PartnerQuotesPage from './pages/PartnerQuotesPage';
import CommissionsPage from './pages/CommissionsPage';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PermissionsProvider } from './context/PermissionsContext';
import { ProjectProvider } from './context/ProjectContext';
import { SocketProvider } from './context/SocketContext';
import { NotificationProvider } from './context/NotificationContext';
import ProtectedRoute from './components/ProtectedRoute';
import { Box, CircularProgress, CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import createAppTheme from './theme';
import DashboardLayout from './layouts/DashboardLayout';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enUS } from 'date-fns/locale';
import ProjectDetailPage from './pages/ProjectDetailPage';
import { isNativeMobilePlatform } from './utils/platform';
import InAppFileViewer from './components/InAppFileViewer';
import QuickExpenseBridge from './components/QuickExpenseBridge';
import { rememberQuickExpensePath } from './services/nativeExpenseService';

const Iso9001Page = lazy(() => import('./pages/Iso9001Page'));
const SecurityCenterPage = lazy(() => import('./pages/SecurityCenterPage'));
const FiscalPage = lazy(() => import('./pages/FiscalPage'));
const NativeMobileRoot = lazy(() => import('./components/mobile/NativeMobileRoot'));
const NdaManagementPage = lazy(() => import('./pages/NdaManagementPage'));
const NdaSigningPage = lazy(() => import('./pages/NdaSigningPage'));
const QuickExpensePage = lazy(() => import('./pages/QuickExpensePage'));

function QuickExpenseRoute() {
    const location = useLocation();
    return <QuickExpensePage key={location.key} forceNew={location.state?.quickExpenseNew === true} />;
}

function QuickExpenseLoginRedirect() {
    const location = useLocation();
    useEffect(() => {
        rememberQuickExpensePath(`/expense-tickets/quick${new URLSearchParams(location.search).get('capture') === '1' ? '?capture=1' : ''}`);
    }, [location.search]);
    return <Navigate to="/login" replace />;
}

const RouteFallback = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
        <CircularProgress />
    </Box>
);

// Componente para el contenido de las rutas
function AppContent({ darkMode, setDarkMode }) {
    const { user } = useAuth();

    return (
        <Routes>
            {/* Login - sin protección */}
            <Route path="/login" element={<LoginPage />} />
            {!user && <Route path="/expense-tickets/quick" element={<QuickExpenseLoginRedirect />} />}
            <Route path="/partner-portal" element={<PartnerPortalPage />} />
            <Route path="/sign/nda/:token" element={<Suspense fallback={<RouteFallback />}><NdaSigningPage /></Suspense>} />

            {/* Ruta raíz - redirige según si hay usuario */}
            <Route
                path="/"
                element={
                    user ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />
                }
            />

            {/* Rutas protegidas con layout */}
            {user && (
                <Route
                    path="/"
                    element={<DashboardLayout darkMode={darkMode} setDarkMode={setDarkMode} />}
                >
                    <Route
                        path="dashboard"
                        element={
                            <ProtectedRoute module="dashboard" requiredPermission="view">
                                <DashboardPage />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="projects"
                        element={
                            <ProtectedRoute module="projects" requiredPermission="view">
                                <ProjectManagement />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="clients"
                        element={
                            <ProtectedRoute module="clients" requiredPermission="view">
                                <ClientManagement />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="tasks"
                        element={
                            <ProtectedRoute module="tasks" requiredPermission="view">
                                <WorkCenterPage />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="followups"
                        element={<Navigate to="/tasks" replace />}
                    />

                    <Route
                        path="workflow"
                        element={<Navigate to="/tasks" replace state={{ workflowRetired: true }} />}
                    />

                    <Route
                        path="notifications"
                        element={
                            <ProtectedRoute module="notifications" requiredPermission="view">
                                <NotificationCenter />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="chat"
                        element={
                            <ProtectedRoute module="chat" requiredPermission="view">
                                <ChatPage />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="calendar"
                        element={
                            <ProtectedRoute module="dashboard" requiredPermission="view">
                                <CalendarPage />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="users"
                        element={
                            <ProtectedRoute module="users" requiredPermission="view">
                                <UserManagement />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="documents"
                        element={
                            <ProtectedRoute module="documents" requiredPermission="view">
                                <DocumentManagement />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="ndas"
                        element={
                            <ProtectedRoute module="ndaAgreements" requiredPermission="view">
                                <Suspense fallback={<RouteFallback />}>
                                    <NdaManagementPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="expense-tickets"
                        element={
                            <ProtectedRoute module="expenseRequests" requiredPermission="view">
                                <ExpenseTicketsPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route path="expense-tickets/quick" element={
                        <ProtectedRoute module="expenseRequests" requiredPermission="create">
                            <Suspense fallback={<RouteFallback />}><QuickExpenseRoute /></Suspense>
                        </ProtectedRoute>
                    } />

                    <Route
                        path="partner-quotes"
                        element={
                            <ProtectedRoute module="partnerQuotes" requiredPermission="view">
                                <PartnerQuotesPage />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="iso-9001"
                        element={
                            <ProtectedRoute module="quality" requiredPermission="view">
                                <Suspense fallback={<RouteFallback />}>
                                    <Iso9001Page />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="security-center"
                        element={
                            <ProtectedRoute module="securityGovernance" requiredPermission="view">
                                <Suspense fallback={<RouteFallback />}>
                                    <SecurityCenterPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="fiscal"
                        element={
                            <ProtectedRoute module="fiscal" requiredPermission="view">
                                <Suspense fallback={<RouteFallback />}>
                                    <FiscalPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="reports"
                        element={
                            <ProtectedRoute module="reports" requiredPermission="view">
                                <ReportGenerator />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="activity-log"
                        element={
                            <ProtectedRoute module="activityLog" requiredPermission="view">
                                <ActivityLog />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="email-history"
                        element={
                            <ProtectedRoute module="emailCenter" requiredPermission="view">
                                <EmailHistoryPage key={user?.id} />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="sales-docs"
                        element={
                            <ProtectedRoute module="billingDocuments" requiredPermission="view">
                                <FinanceManagement
                                    documentsOnly
                                    permissionModule="billingDocuments"
                                    pageTitle="Sales Documents"
                                />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="finance"
                        element={
                            <ProtectedRoute module="finance" requiredPermission="view">
                                <FinanceManagement />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="commissions"
                        element={
                            <ProtectedRoute module="finance" requiredPermission="view">
                                <CommissionsPage />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="roles-permissions"
                        element={
                            <ProtectedRoute module="roles" requiredPermission="view">
                                <RolesPermissions />
                            </ProtectedRoute>
                        }
                    />

                    <Route path="profile" element={<ProfilePage />} />

                    <Route
                        path="settings"
                        element={
                            <ProtectedRoute module="settings" requiredPermission="view">
                                <SettingsPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="projects/:projectId"
                        element={
                            <ProtectedRoute module="projects" requiredPermission="view">
                                <ProjectDetailPage />
                            </ProtectedRoute>
                        }
                    />
                </Route>

            )}

            {/* Catch-all */}
            <Route path="*" element={<Navigate to={user ? "/dashboard" : "/login"} replace />} />
        </Routes>
    );
}

function App() {
    const nativeMobile = isNativeMobilePlatform();
    const [darkMode, setDarkMode] = useState(() => {
        const savedThemeMode = localStorage.getItem('themeMode');
        if (savedThemeMode === 'dark' || savedThemeMode === 'light') {
            return savedThemeMode === 'dark';
        }

        // Compatibilidad con versiones previas que usaban "darkMode".
        const legacyDarkMode = localStorage.getItem('darkMode');
        if (legacyDarkMode === 'true' || legacyDarkMode === 'false') {
            return legacyDarkMode === 'true';
        }

        return false;
    });

    useEffect(() => {
        const handleStorageChange = (e) => {
            if (e.key === 'themeMode' || e.key === 'darkMode') {
                const nextThemeMode = localStorage.getItem('themeMode');
                if (nextThemeMode === 'dark' || nextThemeMode === 'light') {
                    setDarkMode(nextThemeMode === 'dark');
                    return;
                }

                const nextLegacyDarkMode = localStorage.getItem('darkMode');
                if (nextLegacyDarkMode === 'true' || nextLegacyDarkMode === 'false') {
                    setDarkMode(nextLegacyDarkMode === 'true');
                }
                return;
            }

            if (e.key === 'userPreferences') {
                try {
                    const rawPrefs = localStorage.getItem('userPreferences');
                    if (!rawPrefs) return;
                    const parsedPrefs = JSON.parse(rawPrefs);
                    if (typeof parsedPrefs?.dark_mode === 'boolean') {
                        setDarkMode(parsedPrefs.dark_mode);
                    }
                } catch (error) {}
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    useEffect(() => {
        // Persistencia principal usada por toda la app.
        localStorage.setItem('themeMode', darkMode ? 'dark' : 'light');
        // Compatibilidad con código legado.
        localStorage.setItem('darkMode', darkMode ? 'true' : 'false');

        // Si existen preferencias cacheadas, sincronizamos dark_mode ahí también.
        try {
            const rawPrefs = localStorage.getItem('userPreferences');
            if (rawPrefs) {
                const parsedPrefs = JSON.parse(rawPrefs);
                const nextPrefs = { ...parsedPrefs, dark_mode: darkMode };
                localStorage.setItem('userPreferences', JSON.stringify(nextPrefs));
            }
        } catch (error) {}
    }, [darkMode]);

    // Creamos el tema basado en el estado
    const theme = useMemo(() => createAppTheme(darkMode ? 'dark' : 'light'), [darkMode]);

    const appContent = (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enUS}>
            <Box sx={{ bgcolor: 'background.default', minHeight: '100dvh', display: 'flex', flexDirection: 'column' }}>
                <AuthProvider>
                    {/* ✅ ORDEN CORRECTO: Socket debe estar antes de Notification */}
                    <SocketProvider>
                        <NotificationProvider>
                            <PermissionsProvider>
                                <QuickExpenseBridge />
                                <ProjectProvider>
                                    <AppContent darkMode={darkMode} setDarkMode={setDarkMode} />
                                </ProjectProvider>
                            </PermissionsProvider>
                        </NotificationProvider>
                    </SocketProvider>
                </AuthProvider>
            </Box>
        </LocalizationProvider>
    );

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <InAppFileViewer />
            {nativeMobile ? (
                <Suspense fallback={<RouteFallback />}>
                    <NativeMobileRoot darkMode={darkMode}>{appContent}</NativeMobileRoot>
                </Suspense>
            ) : appContent}
        </ThemeProvider>
    );
}

export default App;
